import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Building2, Users, CalendarClock, Receipt, ShieldCheck, Plus, Trash2,
  Pause, Play, LogOut, Sparkles, AlertTriangle, Database, Download, RotateCcw, ExternalLink, UserCog,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

function ManageBranchesDialog({ clinic }) {
  const [open, setOpen] = useState(false);
  const [branches, setBranches] = useState([]);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/clinics/${clinic.id}/branches`).then((r) => setBranches(r.data)).catch(() => {});
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const add = async () => {
    if (!name.trim()) { toast.error("Nama cabang wajib"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/clinics/${clinic.id}/branches`, { name: name.trim(), address: address.trim() || null });
      toast.success(`Cabang "${name}" ditambahkan`);
      setName(""); setAddress(""); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Gagal"); }
    finally { setBusy(false); }
  };
  const remove = async (b) => {
    if (!window.confirm(`Hapus cabang "${b.name}"?`)) return;
    try { await api.delete(`/admin/branches/${b.id}`); toast.success("Cabang dihapus"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Gagal hapus"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`sa-branches-${clinic.id}`} size="sm" variant="ghost" title="Kelola Cabang"><Building2 className="h-3.5 w-3.5" /></Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[560px]" data-testid="manage-branches-dialog">
        <DialogHeader><DialogTitle>Kelola Cabang — {clinic.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border divide-y max-h-64 overflow-y-auto">
            {branches.length === 0 && <div className="p-4 text-sm text-muted-foreground text-center">Belum ada cabang.</div>}
            {branches.map((b) => (
              <div key={b.id} className="flex items-center justify-between p-3" data-testid={`sa-branch-row-${b.id}`}>
                <div>
                  <div className="font-medium text-sm">{b.name}</div>
                  <div className="text-xs text-muted-foreground">{b.address || "—"}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{b.stats?.users ?? 0} user · {b.stats?.patients ?? 0} pasien · {b.stats?.transactions ?? 0} transaksi</div>
                </div>
                <Button size="sm" variant="ghost" className="text-rose-600" data-testid={`sa-branch-del-${b.id}`} onClick={() => remove(b)}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Nama Cabang</Label><Input data-testid="sa-branch-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="MyClinic Bekasi" /></div>
            <div><Label>Alamat</Label><Input data-testid="sa-branch-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Jl. ..." /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Tutup</Button>
          <Button data-testid="sa-branch-add-btn" onClick={add} disabled={busy}><Plus className="h-4 w-4 mr-1" /> Tambah Cabang</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatTile({ icon: Icon, label, value, accent = "" }) {
  return (
    <div className={`rounded-xl border border-border bg-card p-5 ${accent}`}>
      <div className="flex items-center justify-between">
        <Icon className="h-5 w-5 text-muted-foreground" />
      </div>
      <div className="mt-3 text-3xl font-heading">{value ?? "—"}</div>
      <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

function CreateClinicDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", branch_name: "", owner_name: "", owner_email: "", owner_password: "",
    address: "", phone: "", plan: "starter",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/clinics", form);
      toast.success(`Clinic ${form.name} berhasil dibuat`, { description: `Cabang pertama: ${data.branch_name || form.branch_name || form.name}` });
      setOpen(false);
      setForm({ name: "", branch_name: "", owner_name: "", owner_email: "", owner_password: "", address: "", phone: "", plan: "starter" });
      onCreated?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Gagal membuat clinic");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="sa-create-clinic-btn">
          <Plus className="h-4 w-4 mr-2" /> Tambah Clinic Baru
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[540px]">
        <DialogHeader>
          <DialogTitle>Onboard Clinic Baru</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Nama Clinic *</Label>
            <Input data-testid="sa-form-name" value={form.name} onChange={set("name")} placeholder="Aura Beauty Clinic" />
          </div>
          <div>
            <Label>Nama Cabang Pertama *</Label>
            <Input data-testid="sa-form-branch-name" value={form.branch_name} onChange={set("branch_name")} placeholder="Semarang" />
            <p className="text-xs text-muted-foreground mt-1">Cabang pertama otomatis dibuat & tersimpan permanen. Kosongkan untuk memakai nama clinic.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Owner Name *</Label>
              <Input data-testid="sa-form-owner-name" value={form.owner_name} onChange={set("owner_name")} placeholder="Dr. Aura Sari" />
            </div>
            <div>
              <Label>Plan</Label>
              <select
                data-testid="sa-form-plan"
                value={form.plan}
                onChange={set("plan")}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="starter">Starter</option>
                <option value="professional">Professional</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
          </div>
          <div>
            <Label>Owner Email *</Label>
            <Input data-testid="sa-form-owner-email" type="email" value={form.owner_email} onChange={set("owner_email")} placeholder="owner@aura.id" />
          </div>
          <div>
            <Label>Owner Password * <span className="text-xs text-muted-foreground">(min 8, 1 huruf besar, 1 angka)</span></Label>
            <Input data-testid="sa-form-owner-password" type="password" value={form.owner_password} onChange={set("owner_password")} placeholder="••••••••" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>WhatsApp Clinic</Label>
              <Input data-testid="sa-form-phone" value={form.phone} onChange={set("phone")} placeholder="+6281234567890" />
            </div>
            <div>
              <Label>Alamat</Label>
              <Input data-testid="sa-form-address" value={form.address} onChange={set("address")} placeholder="Jl. Senopati 99" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
          <Button data-testid="sa-form-submit" onClick={submit} disabled={busy || !form.name || !form.owner_email || !form.owner_password || !form.owner_name}>
            {busy ? "Membuat..." : "Buat Clinic"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteClinicDialog({ clinic, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (confirmText !== clinic.id) {
      toast.error("Konfirmasi tidak cocok");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/clinics/${clinic.id}?confirm=${clinic.id}`);
      toast.success(`Clinic ${clinic.name} dihapus`, { description: `Records dihapus: ${Object.values(data.deleted).reduce((a, b) => a + b, 0)}` });
      setOpen(false);
      onDeleted?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Gagal hapus");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`sa-delete-${clinic.id}`} variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50">
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" /> Hapus Clinic Permanen
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
            Aksi ini akan menghapus <b>seluruh data</b> clinic <b>{clinic.name}</b> termasuk pasien, booking, transaksi, rekam medis, dan inventory. Tidak bisa di-undo.
          </div>
          <div>
            <Label>Ketik clinic ID untuk konfirmasi:</Label>
            <div className="mt-1 mb-2 px-2 py-1 rounded bg-secondary text-xs font-mono break-all">{clinic.id}</div>
            <Input
              data-testid="sa-delete-confirm-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Paste clinic ID di sini"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
          <Button
            data-testid="sa-delete-confirm-btn"
            variant="destructive"
            onClick={submit}
            disabled={busy || confirmText !== clinic.id}
          >
            {busy ? "Menghapus..." : "Hapus Permanen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetClinicDialog({ clinic, onReset }) {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [scope, setScope] = useState("transactional");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (confirmText !== clinic.id) { toast.error("Konfirmasi tidak cocok"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/clinics/${clinic.id}/reset?scope=${scope}&confirm=${clinic.id}`);
      toast.success(`Reset ${clinic.name} selesai`, { description: `Dokumen dihapus: ${Object.values(data.deleted).reduce((a, b) => a + b, 0)}` });
      setOpen(false); setConfirmText(""); onReset?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Reset gagal");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`sa-reset-${clinic.id}`} variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700 hover:bg-amber-50" title="Reset data clinic">
          <RotateCcw className="h-3.5 w-3.5" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <AlertTriangle className="h-5 w-5" /> Reset Data Clinic
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
            Membersihkan data <b>{clinic.name}</b>. User, cabang, dan settings tetap dipertahankan.
          </div>
          <div>
            <Label>Cakupan</Label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger data-testid="sa-reset-scope" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="transactional">Transaksional (booking/tx/expenses/notif/rekam medis)</SelectItem>
                <SelectItem value="all">SEMUA (+ pasien/treatment/dokter/inventory/promo)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Ketik clinic ID untuk konfirmasi:</Label>
            <div className="mt-1 mb-2 px-2 py-1 rounded bg-secondary text-xs font-mono break-all">{clinic.id}</div>
            <Input data-testid="sa-reset-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="Paste clinic ID" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
          <Button data-testid="sa-reset-confirm-btn" variant="destructive" onClick={submit} disabled={busy || confirmText !== clinic.id}>
            {busy ? "Mereset..." : "Reset Sekarang"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const rp = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");

function DepositTiersManager({ clinics }) {
  const [clinicId, setClinicId] = useState("");
  const [tiers, setTiers] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", deposit_amount: "", cashback_amount: "", free_treatment_reward: "" });

  useEffect(() => { if (!clinicId && clinics.length) setClinicId(clinics[0].id); }, [clinics, clinicId]);

  const hdr = () => ({ headers: { "X-Clinic-Id": clinicId } });
  const load = () => { if (clinicId) api.get("/deposit-tiers", hdr()).then((r) => setTiers(r.data)).catch(() => setTiers([])); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clinicId]);

  const reset = () => { setEditingId(null); setForm({ name: "", deposit_amount: "", cashback_amount: "", free_treatment_reward: "" }); };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!clinicId) { toast.error("Pilih clinic dulu"); return; }
    if (!form.name.trim()) { toast.error("Nama tier wajib diisi"); return; }
    if (!(Number(form.deposit_amount) > 0)) { toast.error("Nominal deposit harus > 0"); return; }
    const payload = {
      name: form.name.trim(),
      deposit_amount: Number(form.deposit_amount),
      cashback_amount: Number(form.cashback_amount) || 0,
      free_treatment_reward: Number(form.free_treatment_reward) || 0,
    };
    setBusy(true);
    try {
      if (editingId) { await api.patch(`/deposit-tiers/${editingId}`, payload, hdr()); toast.success("Tier diperbarui & tersinkron ke POS"); }
      else { await api.post("/deposit-tiers", { ...payload, active: true }, hdr()); toast.success("Tier dibuat & tersinkron ke POS"); }
      reset(); load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Gagal menyimpan tier"); }
    finally { setBusy(false); }
  };

  const edit = (t) => { setEditingId(t.id); setForm({ name: t.name, deposit_amount: t.deposit_amount, cashback_amount: t.cashback_amount, free_treatment_reward: t.free_treatment_reward }); };
  const remove = async (t) => {
    if (!window.confirm(`Hapus tier "${t.name}"?`)) return;
    try { await api.delete(`/deposit-tiers/${t.id}`, hdr()); toast.success("Tier dihapus"); if (editingId === t.id) reset(); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Gagal menghapus"); }
  };

  return (
    <section data-testid="deposit-tiers-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h2 className="text-xs uppercase tracking-widest text-muted-foreground">Skema Deposit & Tier Member</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Atur tier deposit per-clinic. Perubahan langsung tersinkron ke dropdown Top Up di POS.</p>
        </div>
        <div className="w-64">
          <Select value={clinicId} onValueChange={setClinicId}>
            <SelectTrigger data-testid="tier-clinic-select"><SelectValue placeholder="Pilih Clinic" /></SelectTrigger>
            <SelectContent>
              {clinics.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Form */}
        <Card className="lg:col-span-1">
          <CardHeader><CardTitle className="text-base">{editingId ? "Edit Tier" : "Tambah Tier"}</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-3" data-testid="tier-form">
              <div>
                <Label>Nama Tier</Label>
                <Input data-testid="tier-name" className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Silver / Gold / Platinum" />
              </div>
              <div>
                <Label>Nominal Deposit (Dibayar)</Label>
                <Input type="number" data-testid="tier-deposit" className="mt-1" value={form.deposit_amount} onChange={(e) => setForm({ ...form, deposit_amount: e.target.value })} placeholder="1000000" />
              </div>
              <div>
                <Label>Cashback / Bonus Saldo (Rp)</Label>
                <Input type="number" data-testid="tier-cashback" className="mt-1" value={form.cashback_amount} onChange={(e) => setForm({ ...form, cashback_amount: e.target.value })} placeholder="100000" />
              </div>
              <div>
                <Label>Reward Free Treatment (Rp)</Label>
                <Input type="number" data-testid="tier-reward" className="mt-1" value={form.free_treatment_reward} onChange={(e) => setForm({ ...form, free_treatment_reward: e.target.value })} placeholder="150000" />
              </div>
              {(Number(form.deposit_amount) > 0) && (
                <div className="rounded-lg bg-secondary p-2.5 text-xs space-y-0.5">
                  <div>Bayar: <b>{rp(form.deposit_amount)}</b></div>
                  <div>Saldo deposit: <b>{rp(form.deposit_amount)}</b> + Bonus: <b>{rp(form.cashback_amount)}</b></div>
                  <div>Reward Treatment: <b>{rp(form.free_treatment_reward)}</b></div>
                </div>
              )}
              <div className="flex gap-2">
                <Button type="submit" data-testid="tier-save" disabled={busy} className="flex-1"><Plus className="h-4 w-4 mr-1" /> {busy ? "Menyimpan..." : editingId ? "Simpan" : "Tambah"}</Button>
                {editingId && <Button type="button" variant="outline" data-testid="tier-cancel" onClick={reset}>Batal</Button>}
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="lg:col-span-2">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="tiers-table">
                <thead className="bg-secondary/40 text-xs uppercase tracking-widest text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-3">Tier</th>
                    <th className="text-right px-4 py-3">Deposit</th>
                    <th className="text-right px-4 py-3">Cashback</th>
                    <th className="text-right px-4 py-3">Reward Treatment</th>
                    <th className="text-right px-4 py-3">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {tiers.map((t) => (
                    <tr key={t.id} className="border-t border-border" data-testid={`tier-row-${t.id}`}>
                      <td className="px-4 py-3 font-medium">{t.name}</td>
                      <td className="px-4 py-3 text-right">{rp(t.deposit_amount)}</td>
                      <td className="px-4 py-3 text-right text-emerald-600">{rp(t.cashback_amount)}</td>
                      <td className="px-4 py-3 text-right text-amber-600">{rp(t.free_treatment_reward)}</td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="outline" data-testid={`tier-edit-${t.id}`} onClick={() => edit(t)} className="mr-1">Edit</Button>
                        <Button size="sm" variant="ghost" data-testid={`tier-del-${t.id}`} onClick={() => remove(t)} className="text-rose-600"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </td>
                    </tr>
                  ))}
                  {tiers.length === 0 && (<tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Belum ada tier untuk clinic ini.</td></tr>)}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}


export default function SuperAdmin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshAt, setRefreshAt] = useState(0);

  const refresh = () => setRefreshAt((n) => n + 1);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/clinics"),
    ])
      .then(([s, c]) => {
        setStats(s.data);
        setClinics(c.data);
      })
      .catch(() => toast.error("Gagal load data admin"))
      .finally(() => setLoading(false));
  }, [refreshAt]);

  const toggleStatus = async (c) => {
    const next = c.status === "active" ? "suspended" : "active";
    try {
      await api.patch(`/admin/clinics/${c.id}`, { status: next });
      toast.success(`Clinic ${next === "active" ? "diaktifkan" : "di-suspend"}`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const exportClinic = async (c) => {
    const token = localStorage.getItem("myclinic_token");
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/clinics/${c.id}/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("export gagal");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `myclinic_export_${c.slug || c.id}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Data ${c.name} diekspor`);
    } catch {
      toast.error("Gagal ekspor data");
    }
  };

  // ---- Cross-tenant user management ----
  const [users, setUsers] = useState([]);
  const [usersClinic, setUsersClinic] = useState("");
  const loadUsers = () => {
    const params = usersClinic ? `?clinic_id=${usersClinic}` : "";
    api.get(`/admin/users${params}`).then((r) => setUsers(r.data)).catch(() => {});
  };
  useEffect(() => { loadUsers(); }, [refreshAt, usersClinic]);
  const changeRole = async (u, role) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { role });
      toast.success(`Role ${u.name} diubah ke ${role}`);
      loadUsers();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Gagal ubah role");
    }
  };

  // ---- Multi-clinic access group (owner/cs/crm can span several clinics) ----
  const [accessTarget, setAccessTarget] = useState(null);
  const [selAccess, setSelAccess] = useState([]);
  const [accessBusy, setAccessBusy] = useState(false);
  const openAccess = (u) => {
    setAccessTarget(u);
    const base = (u.clinic_ids && u.clinic_ids.length > 0) ? u.clinic_ids : (u.clinic_id ? [u.clinic_id] : []);
    setSelAccess(base);
  };
  const toggleAccess = (cid) => setSelAccess((s) => s.includes(cid) ? s.filter((x) => x !== cid) : [...s, cid]);
  const saveAccess = async () => {
    setAccessBusy(true);
    try {
      await api.patch(`/admin/users/${accessTarget.id}`, { clinic_ids: selAccess });
      toast.success(`Akses klinik ${accessTarget.name} diperbarui (${selAccess.length} klinik)`);
      setAccessTarget(null);
      loadUsers();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Gagal menyimpan akses");
    } finally { setAccessBusy(false); }
  };

  const [backups, setBackups] = useState(null);
  const [backingUp, setBackingUp] = useState(false);
  const loadBackups = () => api.get("/admin/backups").then((r) => setBackups(r.data)).catch(() => {});
  useEffect(() => { loadBackups(); }, []);
  const runBackup = async () => {
    setBackingUp(true);
    try {
      const { data } = await api.post("/admin/backups/run");
      toast.success(`Backup dibuat: ${data.file} (${(data.size_bytes / 1024).toFixed(0)} KB)`);
      loadBackups();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Backup gagal");
    } finally {
      setBackingUp(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-20">
        <div className="flex h-16 items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <div className="grid h-9 w-9 place-items-center rounded-lg bg-red-600 text-white">
              <ShieldCheck className="h-4 w-4" />
            </div>
            <div>
              <div className="font-heading text-base tracking-tight">MyClinic — Super Admin</div>
              <div className="text-[10px] uppercase tracking-widest text-red-600 font-semibold">
                System-wide Console
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden md:block">
              <div className="text-sm font-medium">{user?.name}</div>
              <div className="text-[10px] text-muted-foreground">{user?.email}</div>
            </div>
            <Button data-testid="sa-open-clinic" variant="outline" size="sm" onClick={() => navigate("/bookings")}>
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Panel Clinic
            </Button>
            <Button data-testid="sa-logout" variant="outline" size="sm" onClick={() => { logout(); navigate("/login"); }}>
              <LogOut className="h-3.5 w-3.5 mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="px-6 py-8 max-w-7xl mx-auto space-y-8">
        {/* Global stats */}
        <section>
          <h2 className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Global Stats</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <StatTile icon={Building2} label="Total Clinic" value={stats?.total_clinics} accent="border-l-4 border-l-foreground" />
            <StatTile icon={Sparkles} label="Active" value={stats?.active_clinics} accent="border-l-4 border-l-emerald-500" />
            <StatTile icon={Pause} label="Suspended" value={stats?.suspended_clinics} accent="border-l-4 border-l-amber-500" />
            <StatTile icon={Users} label="Total User" value={stats?.total_users} />
            <StatTile icon={Users} label="Total Pasien" value={stats?.total_patients} />
            <StatTile icon={CalendarClock} label="Total Booking" value={stats?.total_bookings} />
            <StatTile icon={Receipt} label="Total Tx" value={stats?.total_transactions} />
          </div>
        </section>

        {/* Deposit tiers */}
        <DepositTiersManager clinics={clinics} />

        {/* Clinics */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-xs uppercase tracking-widest text-muted-foreground">Daftar Clinic</h2>
              <p className="text-sm text-muted-foreground mt-0.5">Setiap clinic memiliki data, user, dan settings terisolasi.</p>
            </div>
            <CreateClinicDialog onCreated={refresh} />
          </div>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="sa-clinics-table">
                  <thead className="bg-secondary/40 text-xs uppercase tracking-widest text-muted-foreground">
                    <tr>
                      <th className="text-left px-4 py-3">Clinic</th>
                      <th className="text-left px-4 py-3">Owner</th>
                      <th className="text-left px-4 py-3">Plan</th>
                      <th className="text-left px-4 py-3">Status</th>
                      <th className="text-right px-4 py-3">Users</th>
                      <th className="text-right px-4 py-3">Pasien</th>
                      <th className="text-right px-4 py-3">Booking</th>
                      <th className="text-right px-4 py-3">Tx</th>
                      <th className="text-right px-4 py-3">Created</th>
                      <th className="text-right px-4 py-3">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr><td colSpan={10} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>
                    )}
                    {!loading && clinics.length === 0 && (
                      <tr><td colSpan={10} className="px-4 py-8 text-center text-muted-foreground">Belum ada clinic terdaftar.</td></tr>
                    )}
                    {clinics.map((c) => (
                      <tr key={c.id} className="border-t border-border hover:bg-secondary/20" data-testid={`sa-clinic-row-${c.id}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium">{c.name}</div>
                          <div className="text-[10px] text-muted-foreground font-mono">{c.id.slice(0, 8)}…</div>
                        </td>
                        <td className="px-4 py-3 text-xs">{c.owner_email || "—"}</td>
                        <td className="px-4 py-3 capitalize">{c.plan || "starter"}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider ${
                            c.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                          }`}>{c.status || "active"}</span>
                        </td>
                        <td className="px-4 py-3 text-right">{c.stats?.users ?? "—"}</td>
                        <td className="px-4 py-3 text-right">{c.stats?.patients ?? "—"}</td>
                        <td className="px-4 py-3 text-right">{c.stats?.bookings ?? "—"}</td>
                        <td className="px-4 py-3 text-right">{c.stats?.transactions ?? "—"}</td>
                        <td className="px-4 py-3 text-right text-xs text-muted-foreground">
                          {c.created_at ? new Date(c.created_at).toLocaleDateString("id-ID") : "—"}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              data-testid={`sa-export-${c.id}`}
                              size="sm"
                              variant="ghost"
                              onClick={() => exportClinic(c)}
                              title="Export data (Excel)"
                            >
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                            <ResetClinicDialog clinic={c} onReset={refresh} />
                            <ManageBranchesDialog clinic={c} />
                            <Button
                              data-testid={`sa-toggle-${c.id}`}
                              size="sm"
                              variant="ghost"
                              onClick={() => toggleStatus(c)}
                              title={c.status === "active" ? "Suspend" : "Aktifkan"}
                            >
                              {c.status === "active" ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                            </Button>
                            <DeleteClinicDialog clinic={c} onDeleted={refresh} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* User management (cross-tenant) */}
        <section data-testid="sa-users-section">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><UserCog className="h-3.5 w-3.5" /> Manajemen User & Role</h2>
              <p className="text-sm text-muted-foreground mt-0.5">Atur role setiap user di seluruh clinic. Super Admin tidak dapat diubah dari sini.</p>
            </div>
            <Select value={usersClinic || "all"} onValueChange={(v) => setUsersClinic(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="sa-users-clinic-filter" className="w-[220px]"><SelectValue placeholder="Semua Clinic" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Clinic</SelectItem>
                {clinics.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="sa-users-table">
                  <thead className="bg-secondary/40 text-xs uppercase tracking-widest text-muted-foreground">
                    <tr>
                      <th className="text-left px-4 py-3">Nama</th>
                      <th className="text-left px-4 py-3">Email</th>
                      <th className="text-left px-4 py-3">Clinic</th>
                      <th className="text-left px-4 py-3">Role</th>
                      <th className="text-left px-4 py-3">Akses Multi-Klinik</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length === 0 && (
                      <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Tidak ada user.</td></tr>
                    )}
                    {users.map((u) => (
                      <tr key={u.id} className="border-t border-border hover:bg-secondary/20" data-testid={`sa-user-row-${u.id}`}>
                        <td className="px-4 py-3 font-medium">{u.name}</td>
                        <td className="px-4 py-3 text-xs">{u.email}</td>
                        <td className="px-4 py-3 text-xs">{u.clinic_name || "—"}</td>
                        <td className="px-4 py-3">
                          <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                            <SelectTrigger data-testid={`sa-user-role-${u.id}`} className="w-[160px] h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {["owner", "manager", "fo_cs", "cs", "crm", "doctor", "therapist"].map((r) => (
                                <SelectItem key={r} value={r}>{r}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">
                              {(u.clinic_ids && u.clinic_ids.length > 1) ? `${u.clinic_ids.length} klinik` : "1 klinik"}
                            </span>
                            <Button data-testid={`sa-user-access-${u.id}`} size="sm" variant="outline" className="h-8" onClick={() => openAccess(u)}>
                              <Building2 className="mr-1.5 h-3.5 w-3.5" /> Atur
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Multi-clinic access dialog */}
        <Dialog open={!!accessTarget} onOpenChange={(o) => !o && setAccessTarget(null)}>
          <DialogContent data-testid="sa-access-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Building2 className="h-5 w-5" /> Akses Multi-Klinik</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Pilih klinik yang boleh diakses & di-switch oleh <b>{accessTarget?.name || accessTarget?.email}</b> lewat dropdown di header.
              Pilih lebih dari satu (mis. Reglow Bekasi, Solo, Sidoarjo) agar akun ini bisa berpindah antar klinik seperti Super Admin.
            </p>
            <div className="flex flex-wrap gap-2 max-h-[50vh] overflow-y-auto">
              {clinics.map((c) => {
                const on = selAccess.includes(c.id);
                return (
                  <button type="button" key={c.id} data-testid={`sa-access-opt-${c.id}`} onClick={() => toggleAccess(c.id)}
                    className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${on ? "bg-foreground text-background border-foreground" : "bg-background hover:bg-secondary"}`}>
                    {c.name}
                  </button>
                );
              })}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAccessTarget(null)}>Batal</Button>
              <Button data-testid="sa-access-save" onClick={saveAccess} disabled={accessBusy}>{accessBusy ? "Menyimpan..." : "Simpan Akses"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Database backups */}
        <section data-testid="sa-backups-section">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-xs uppercase tracking-widest text-muted-foreground">Backup Database</h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                Otomatis tiap {backups?.interval_hours ?? 24} jam · menyimpan {backups?.retention ?? 7} backup terakhir.
              </p>
            </div>
            <Button data-testid="sa-run-backup-btn" size="sm" onClick={runBackup} disabled={backingUp}>
              <Database className="h-3.5 w-3.5 mr-1.5" /> {backingUp ? "Membuat..." : "Backup Sekarang"}
            </Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <table className="w-full text-sm" data-testid="sa-backups-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-3">File</th>
                    <th className="px-4 py-3">Ukuran</th>
                    <th className="px-4 py-3">Dibuat</th>
                  </tr>
                </thead>
                <tbody>
                  {(backups?.backups || []).map((b) => (
                    <tr key={b.file} className="border-t border-border" data-testid={`sa-backup-${b.file}`}>
                      <td className="px-4 py-3 font-mono text-xs">{b.file}</td>
                      <td className="px-4 py-3">{(b.size_bytes / 1024).toFixed(0)} KB</td>
                      <td className="px-4 py-3 text-muted-foreground">{new Date(b.created_at).toLocaleString("id-ID")}</td>
                    </tr>
                  ))}
                  {(!backups?.backups || backups.backups.length === 0) && (
                    <tr><td colSpan={3} className="px-4 py-6 text-center text-muted-foreground">Belum ada backup.</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </section>

        <section className="rounded-xl border border-border bg-secondary/40 p-5">
          <div className="flex items-start gap-3">
            <ShieldCheck className="h-5 w-5 text-emerald-600 mt-0.5" />
            <div className="text-sm space-y-1.5">
              <div className="font-medium">Keamanan Multi-Tenant Aktif</div>
              <div className="text-muted-foreground">
                Semua endpoint API otomatis menyaring data berdasarkan <code className="text-xs">clinic_id</code> user yang login. User dari satu clinic <b>tidak dapat</b> mengakses pasien, booking, atau data finansial clinic lain — secara teknis tervalidasi di setiap query MongoDB.
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
